import { Owner } from "solid-js";
import Cookies from "js-cookie";

import CreditLine from "@repo/kikoff-models/lending/CreditLine";
import Subscription from "@repo/kikoff-models/Subscription";
import Transaction from "@repo/kikoff-models/Transaction";
import Analytics, { StructuredEvents } from "@repo/utils/Analytics";
import UObject from "@repo/utils/UObject";
import Platform from "@repo/utils-client/Platform";
import AppRouter from "@repo/utils-solid/AppRouter";
import SolidAnalytics from "@repo/utils-solid/SolidAnalytics";

import { appApi } from "~/rest.js";

import events from "./events.js";

export type AnalyticsEvents = {
  conversion: {
    Signup: {};
    "Credit Account Open": {
      tier: Subscription.Tier;
      accountId: CreditLine.Id;
    };
    "Make Credit Line Payment": {
      tier: Subscription.Tier;
      accountId: CreditLine.Id;
      transactionId: Transaction.Id;
    };
    // Only snapchat uses login event
    SnapchatOnlyLogin: {};
  };
  analytics: StructuredEvents.Flatten<typeof events>;
  other: {
    // Not meant to be populated
  };
};

const base = new Analytics<
  AnalyticsEvents,
  {
    owner: Owner;
  }
>();

export declare namespace Registration {
  type Analytics = Analytics.Client.Registration.Sync<typeof base, "analytics">;
  type Conversion = Analytics.Client.Registration.Sync<
    typeof base,
    "conversion"
  >;
  type Other = Analytics.Client.Registration.Sync<typeof base, "other">;
}

const clients =
  // prettier-ignore
  import.meta.glob("./clients/*/*.ts", {
  eager: true,
  import: "default",
}) as {
/* GENERATED */ "./clients/analytics/amplitude.ts": typeof import("./clients/analytics/amplitude.ts")["default"],
/* GENERATED */ "./clients/analytics/clarity.ts": typeof import("./clients/analytics/clarity.ts")["default"],
/* GENERATED */ "./clients/conversion/facebook.ts": typeof import("./clients/conversion/facebook.ts")["default"],
/* GENERATED */ "./clients/conversion/googleAnalytics.ts": typeof import("./clients/conversion/googleAnalytics.ts")["default"],
/* GENERATED */ "./clients/conversion/googleTagManager.ts": typeof import("./clients/conversion/googleTagManager.ts")["default"],
/* GENERATED */ "./clients/conversion/impactRadius.ts": typeof import("./clients/conversion/impactRadius.ts")["default"],
/* GENERATED */ "./clients/conversion/podscribe.ts": typeof import("./clients/conversion/podscribe.ts")["default"],
/* GENERATED */ "./clients/conversion/snapchat.ts": typeof import("./clients/conversion/snapchat.ts")["default"],
/* GENERATED */ "./clients/conversion/tiktok.ts": typeof import("./clients/conversion/tiktok.ts")["default"],
/* GENERATED */ "./clients/other/sentry.ts": typeof import("./clients/other/sentry.ts")["default"]
};

const analytics = !Platform.isClient
  ? (base as never)
  : base
      .authorize(async () => {
        if (import.meta.env.MOBILE) return true;

        if (Cookies.get("consentCookie")) {
          return Cookies.get("consentCookie") === "optIn";
        }

        // only allow analytics if user has opted in or is not in Quebec
        return (await appApi.region.GET({})).region !== "QC";
      })
      .setBaseProperties(() => ({
        solidjs: true,
        path: (() => {
          // We cannot always guarantee analytics.track won't be called before
          // AppRouter is registered. If it is AppRouter will throw, fall back
          // to window path
          try {
            return AppRouter.pathname;
          } catch (e) {
            return window.location.pathname;
          }
        })(),
        "user agent": window.navigator.userAgent,
      }))
      .addClients(
        SolidAnalytics.wrapClients(
          UObject.trimKeys(clients, "./clients/", ".ts"),
          () => base.context.owner
        )
      );

export default analytics;

export const track = StructuredEvents.createTrackProxy(analytics, events);

export const trackImpression = SolidAnalytics.createTrackImpressionProxy(
  analytics,
  events
);
